"use client"

import { useEffect, useState } from "react"
import Bet from "./Bet"
import Filters from "./Filters"
import { db } from "../firebase"
import { collection, getDocs, query, where, updateDoc, doc, arrayUnion, arrayRemove, getDoc } from "firebase/firestore"
import { useAuth } from "../context/AuthContext"
import { sendNotification } from "../database/Functions"
import { v4 as uuidv4 } from "uuid"
import Header from "./header"
import { Users, Search, UserPlus } from "lucide-react"

function FriendsBets() {
  const [friendsBets, setFriendsBets] = useState([])
  const [filteredBets, setFilteredBets] = useState([])
  const [friendProfiles, setFriendProfiles] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [loading, setLoading] = useState(true)
  const [suggestedProfiles, setSuggestedProfiles] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)
  const { currentUser, userProfile } = useAuth()
  const [currentView, setCurrentView] = useState("settings") // Default view
  const [showNotifications, setShowNotifications] = useState(false)

  // Fetch friends' bets and profiles
  const fetchFriendsBets = async (friends) => {
    if (currentUser) {
      try {
        setLoading(true)
        const finalResults = []
        const tmpFriendProfiles = []

        for (const friendEmail of friends) {
          if (friendEmail === userProfile?.email) continue

          const q = query(collection(db, "users"), where("email", "==", friendEmail))
          const querySnapshot = await getDocs(q)
          for (const doc of querySnapshot.docs) {
            const friendData = doc.data()
            friendData.id = doc.id

            // Add friend request status
            const friendRequestStatus = await fetchFriendRequestStatus(friendData)
            friendData.requestedMe = friendRequestStatus.requestedMe
            friendData.alreadyRequested = friendRequestStatus.alreadyRequested

            tmpFriendProfiles.push(friendData)

            const q2 = query(collection(db, "ActiveBets"), where("user_id", "==", doc.id))
            const querySnapshot2 = await getDocs(q2)
            querySnapshot2.forEach((doc2) => {
              const betData = doc2.data()
              betData.id = doc2.id

              var currentTime = new Date()
              var betTime = new Date(betData["bet_time"])

              if (betData["accepted"] === "No" && betData["status"] === "Created" && betTime >= currentTime) {
                finalResults.push(betData)
              }
            })
          }
        }
        finalResults.sort((a, b) => new Date(a.bet_time) - new Date(b.bet_time))
        setFriendProfiles(tmpFriendProfiles)
        setFriendsBets(finalResults)
        setFilteredBets(finalResults)
        setLoading(false)
      } catch (error) {
        console.error("Error fetching bets:", error)
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchFriendsBets(userProfile?.friends)
  }, [currentUser, userProfile?.friends])

  const fetchPeopleYouMayKnow = async () => {
    if (!currentUser || !userProfile) return
    setLoadingSuggestions(true)
    try {
      const allUsersSnapshot = await getDocs(collection(db, "users"))
      const allUsers = allUsersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))

      const myEmail = (currentUser.email || "").toLowerCase()
      const myFriends = new Set((userProfile.friends || []).map((e) => (e || "").toLowerCase()))

      const usersByEmail = new Map()
      allUsers.forEach((u) => {
        const email = (u.email || "").toLowerCase()
        if (email) usersByEmail.set(email, u)
      })

      const friendsOfFriends = new Set()
      myFriends.forEach((friendEmail) => {
        const friendUser = usersByEmail.get(friendEmail)
        if (!friendUser || !Array.isArray(friendUser.friends)) return
        friendUser.friends.forEach((fofEmailRaw) => {
          const fofEmail = (fofEmailRaw || "").toLowerCase()
          if (!fofEmail) return
          if (fofEmail === myEmail) return
          if (myFriends.has(fofEmail)) return
          friendsOfFriends.add(fofEmail)
        })
      })

      const candidates = allUsers
        .filter((u) => {
          const email = (u.email || "").toLowerCase()
          if (!email) return false
          if (email === myEmail) return false
          if (myFriends.has(email)) return false
          return true
        })
        .sort((a, b) => {
          const aEmail = (a.email || "").toLowerCase()
          const bEmail = (b.email || "").toLowerCase()
          const aPri = friendsOfFriends.has(aEmail) ? 0 : 1
          const bPri = friendsOfFriends.has(bEmail) ? 0 : 1
          if (aPri !== bPri) return aPri - bPri
          const aName = (a.full_name || a.username || a.email || "").toLowerCase()
          const bName = (b.full_name || b.username || b.email || "").toLowerCase()
          return aName.localeCompare(bName)
        })
        .slice(0, 10)

      const enriched = await Promise.all(
        candidates.map(async (u) => {
          const status = await fetchFriendRequestStatus(u)
          return {
            ...u,
            requestedMe: status.requestedMe,
            alreadyRequested: status.alreadyRequested,
            isFriendOfFriend: friendsOfFriends.has((u.email || "").toLowerCase()),
          }
        })
      )

      setSuggestedProfiles(enriched)
    } catch (error) {
      console.error("Error fetching people you may know:", error)
      setSuggestedProfiles([])
    } finally {
      setLoadingSuggestions(false)
    }
  }

  useEffect(() => {
    fetchPeopleYouMayKnow()
  }, [currentUser, userProfile?.friends, userProfile?.email])

  const handleFilterSelect = (sportsKey, sportsTitle) => {
    if (sportsKey === "all") {
      setFilteredBets(friendsBets)
    } else {
      const filtered = friendsBets.filter(
        (bet) => bet.bet_sport_key === sportsKey || bet.bet_sport_title === sportsTitle,
      )
      setFilteredBets(filtered)
    }
  }

  const handleBetAccepted = (betId) => {
    setFilteredBets((prevBets) => prevBets.filter((bet) => bet.id !== betId))
  }

  const handleSearch = async (searchParam) => {
    if (!searchParam || searchParam === "") {
      // Reset search if query is empty
      fetchFriendsBets(userProfile?.friends)
      return
    }

    try {
      setLoading(true)
      const usersCollection = collection(db, "users")

      // Fetch all documents (you can limit results if necessary)
      const querySnapshot = await getDocs(usersCollection)

      // Convert searchParam to lowercase for case-insensitive comparison
      const lowercasedSearch = searchParam.toLowerCase()

      // Filter results locally
      const result = querySnapshot.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter((user) => {
          const fullName = user.full_name || ""
          const username = user.username || ""
          const email = user.email || ""

          return (
            fullName.toLowerCase().includes(lowercasedSearch) ||
            username.toLowerCase().includes(lowercasedSearch) ||
            email.toLowerCase().includes(lowercasedSearch)
          )
        })

      // Add friend request status for each result
      for (let i = 0; i < result.length; i++) {
        const friendRequestStatus = await fetchFriendRequestStatus(result[i])
        result[i].requestedMe = friendRequestStatus.requestedMe
        result[i].alreadyRequested = friendRequestStatus.alreadyRequested
      }

      setFriendProfiles(result)
      setLoading(false)
    } catch (error) {
      console.error("Error performing search:", error)
      setLoading(false)
    }
  }

  const fetchFriendRequestStatus = async (friend) => {
    var alreadyRequested = false
    var requestedMe = false

    var friendStatus = { alreadyRequested, requestedMe }

    try {
      const notificationsRef = collection(db, 'Notifications')
      const q = query(
        notificationsRef,
        where('fromUid', '==', currentUser.uid),
        where('type', '==', 'Request'),
        where('toUid', '==', friend.id),
      )

      const querySnapshot = await getDocs(q)

      const notifications = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      if (notifications.length) {
        alreadyRequested = true
      }

      const notificationsRef2 = collection(db, 'Notifications')
      const q2 = query(
        notificationsRef2,
        where('toUid', '==', currentUser.uid),
        where('type', '==', 'Request'),
        where('fromUid', '==', friend.id),
      )

      const querySnapshot2 = await getDocs(q2)

      const notifications2 = querySnapshot2.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      if (notifications2.length) {
        requestedMe = true
      }

      friendStatus.alreadyRequested = alreadyRequested
      friendStatus.requestedMe = requestedMe

    } catch (error) {
      console.error('Error fetching notifications:', error)
      return { alreadyRequested: false, requestedMe: false }
    }

    return friendStatus
  }

  const handleDeleteFriend = async (friendEmail) => {
    try {
      // References to the current user and friend's documents
      const currentUserRef = doc(db, 'users', currentUser.uid)
      const friendRef = query(collection(db, 'users'), where('email', '==', friendEmail))
      const friendSnapshot = await getDocs(friendRef)

      if (!friendSnapshot.empty) {
        const friendDoc = friendSnapshot.docs[0] // Assuming unique emails
        const friendDocRef = doc(db, 'users', friendDoc.id)

        // Remove friend from the current user's friends list
        await updateDoc(currentUserRef, {
          friends: arrayRemove(friendEmail),
        })

        // Remove current user from the friend's friends list
        await updateDoc(friendDocRef, {
          friends: arrayRemove(currentUser.email),
        })

        console.log(`Friendship with ${friendEmail} has been successfully deleted.`)
      } else {
        console.error('Friend not found.')
      }
    } catch (error) {
      console.error('Error deleting friend:', error)
    }
    setSearchQuery("")
    setFriendProfiles([])
    fetchFriendsBets(userProfile?.friends)
  }

  const handleFriendAction = async (friend) => {
    const isFriend = userProfile?.friends.includes(friend.email)
    var alreadyRequested = false
    var requestedMe = false

    try {
      const notificationsRef = collection(db, 'Notifications')
      const q = query(
        notificationsRef,
        where('fromUid', '==', currentUser.uid),
        where('type', '==', 'Request'),
        where('toUid', '==', friend.id),
      )

      const querySnapshot = await getDocs(q)

      const notifications = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      if (notifications.length) {
        alreadyRequested = true
      }

      const notificationsRef2 = collection(db, 'Notifications')
      const q2 = query(
        notificationsRef2,
        where('toUid', '==', currentUser.uid),
        where('type', '==', 'Request'),
        where('fromUid', '==', friend.id),
      )

      const querySnapshot2 = await getDocs(q2)

      const notifications2 = querySnapshot2.docs.map(doc => ({ id: doc.id, ...doc.data() }))

      if (notifications2.length) {
        requestedMe = true
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
      return
    }

    if (isFriend) {
      handleDeleteFriend(friend.email)
      alert("Removed Friend!")
    } else if (requestedMe) {
      console.log("friend prof are ", friendProfiles)
      var friend = friendProfiles[0]

      const fromUserRef = doc(db, 'users', friend.id)
      const fromUserSnapshot = await getDoc(fromUserRef)

      if (!fromUserSnapshot.exists()) {
        console.error('User not found.')
        return
      }

      const fromUserData = fromUserSnapshot.data()

      // Add the users as friends to each other
      const currentUserRef = doc(db, 'users', currentUser.uid)

      // Add the "from" user to the current user's friends list
      await updateDoc(currentUserRef, {
        friends: arrayUnion(fromUserData.email), // Use the current user's email
      })

      // Add the current user to the "from" user's friends list
      await updateDoc(fromUserRef, {
        friends: arrayUnion(currentUser.email),
      })

      alert(`Added Friend`)
      setSearchQuery("")
      setFriendProfiles([])
      fetchFriendsBets(userProfile?.friends)
    } else if (alreadyRequested) {
      alert("You already sent them a friend request")
    } else {
      try {
        // Fetch the friend's UID based on email
        const q = query(collection(db, "users"), where("email", "==", friend.email))
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          const friendDoc = querySnapshot.docs[0]
          const friendUid = friendDoc.id // Use the document ID as UID

          const key = uuidv4()

          // Send notification
          await sendNotification(
            "Request",
            userProfile?.full_name,
            currentUser.uid,
            friendUid, // Pass the friend's UID
            "sent you a friend request",
            "FriendsBets",
            userProfile?.profile_picture,
          )

          alert("Sent a friend request.")
          setSearchQuery("")
          setFriendProfiles([])
          fetchFriendsBets(userProfile?.friends)
        } else {
          console.error('Friend not found')
          setSearchQuery("")
          setFriendProfiles([])
          fetchFriendsBets(userProfile?.friends)
        }
      } catch (error) {
        console.error('Error adding friend:', error)
        setSearchQuery("")
        setFriendProfiles([])
        fetchFriendsBets(userProfile?.friends)
      }
    }
    setSearchQuery("")
    setFriendProfiles([])
    fetchFriendsBets(userProfile?.friends)
  }

  return (
    <div className="bg-gradient-to-b from-gray-900 to-black">
      <Header onToggle={setCurrentView} currentView="notifications" onShowNotifications={setShowNotifications} />
      <div className="w-full flex flex-col items-center bg-gradient-to-b from-gray-900 to-black min-h-screen py-6">
        <div className="w-full max-w-4xl bg-gray-900/30 backdrop-blur-sm p-6 rounded-xl border border-gray-800 shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
              <Users className="w-6 h-6 text-emerald-400 mr-2" />
              Friends
            </h2>

            <div className="relative mb-6">
              <input
                type="text"
                placeholder="Search for friends..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  handleSearch(e.target.value)
                }}
                className="w-full p-3 pl-10 rounded-lg bg-gray-800/70 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <button
                onClick={() => handleSearch(searchQuery)}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-emerald-500 hover:bg-emerald-600 text-white p-1 rounded-md transition-colors"
              >
                <Search className="h-4 w-4" />
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
                <span className="ml-3 text-white">Loading...</span>
              </div>
            ) : friendProfiles.length > 0 ? (
              <div className="space-y-3">
                {friendProfiles.map((friend, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 rounded-lg bg-gray-800/50 border border-gray-700 hover:border-emerald-500/30 transition-all duration-300"
                  >
                    <img
                      src={
                        friend.profile_picture ||
                        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
                      }
                      alt={friend.full_name}
                      className="w-12 h-12 rounded-full border border-gray-700"
                    />
                    <div className="ml-4">
                      <span className="text-white font-medium">{friend.full_name}</span>
                      <p className="text-gray-400 text-sm">{friend.email}</p>
                    </div>
                    <button
                      onClick={() => handleFriendAction(friend)}
                      className={`ml-auto px-4 py-1 rounded-full text-xs transition-colors flex items-center ${
                        userProfile?.friends.includes(friend.email)
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : friend.alreadyRequested
                          ? "bg-gray-500 text-black"
                          : friend.requestedMe
                          ? "bg-green-500 hover:bg-green-600 text-white"
                          : "bg-emerald-500 hover:bg-emerald-600 text-white"
                      }`}
                    >
                      {userProfile?.friends.includes(friend.email) ? (
                        "Remove"
                      ) : friend.alreadyRequested ? (
                        "Requested"
                      ) : friend.requestedMe ? (
                        "Accept"
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Add
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="bg-gray-800/70 p-4 rounded-xl inline-block mb-4">
                  <Users className="w-12 h-12 text-gray-500 mx-auto" />
                </div>
                <h2 className="text-xl font-medium mb-2 text-white">No Friends Found</h2>
                <p className="text-gray-400">Try searching for someone by name</p>
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-xl font-semibold text-white mb-2">People You May Know</h3>
              <p className="text-sm text-gray-400 mb-4">
                Suggested from friends of friends, plus other users you are not connected with yet.
              </p>

              {loadingSuggestions ? (
                <div className="flex items-center gap-3 text-sm text-gray-300 py-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-emerald-500" />
                  Loading suggestions...
                </div>
              ) : suggestedProfiles.length > 0 ? (
                <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                  {suggestedProfiles.map((person) => (
                    <div
                      key={person.id}
                      className="snap-start min-w-[220px] max-w-[220px] rounded-xl border border-gray-700 bg-gray-800/40 p-3 hover:border-emerald-500/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={
                            person.profile_picture ||
                            "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_960_720.png"
                          }
                          alt={person.full_name || person.email}
                          className="w-11 h-11 rounded-full border border-gray-700 object-cover"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white truncate">
                            {person.full_name || person.username || person.email}
                          </p>
                          <p className="text-xs text-gray-400 truncate">@{person.username || "user"}</p>
                        </div>
                      </div>
                      <p className="mt-2 text-[11px] text-gray-400 truncate">
                        {person.isFriendOfFriend ? "Friend of a friend" : "Suggested user"}
                      </p>
                      <button
                        onClick={() => handleFriendAction(person)}
                        className={`mt-3 w-full px-3 py-1.5 rounded-full text-xs transition-colors flex items-center justify-center ${
                          userProfile?.friends.includes(person.email)
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : person.alreadyRequested
                            ? "bg-gray-500 text-black"
                            : person.requestedMe
                            ? "bg-green-500 hover:bg-green-600 text-white"
                            : "bg-emerald-500 hover:bg-emerald-600 text-white"
                        }`}
                      >
                        {userProfile?.friends.includes(person.email) ? (
                          "Remove"
                        ) : person.alreadyRequested ? (
                          "Requested"
                        ) : person.requestedMe ? (
                          "Accept Request"
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-1" />
                            Add Friend
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400 py-3">No suggestions available right now.</div>
              )}
            </div>
          </div>

          {friendsBets.length > 0 && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-white mb-4">Friends' Bets</h2>
              <Filters onFilterSelect={handleFilterSelect} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                {filteredBets.map((bet) => (
                  <Bet key={bet.id} bet={bet} onBetAccepted={handleBetAccepted} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FriendsBets
